import {VNode} from 'snabbdom/vnode';

import type {ContextPanelData} from 'common/widgets/context-panel/types';
import {Placement} from 'common/components/tooltip/types';
import {TooltipAttrs} from 'common/components/tooltip/types';

import type {Position, SelectionState} from './constants';
/**
 * NOTE: Please keep the properties in items menu types generic enough such that they could be used for any list.
 * Not just events or properties. Being generic means we could re-use it in more places and be consistent.
 * We should aim for items-menu as the defacto item list renderer at mixpanel.
 */

export type SectionsFromSearchInputFn = (input: string, currentSections?: Section[]) => Section[];
export type SectionItemContextPanelTransform = (item: SectionItem) => ContextPanelData;
export type SearchMatchResult = {score: number; matchParts: string[]; matchStr: string};
export type CustomSearchMatcherFn = (item: SectionItem, searchString: string) => SearchMatchResult;
export enum LabelTheme {
  Strong = `strong`,
  Soft = `soft`,
  Italic = `italic`,
  InvertedCheckmark = `inverted-checkmark`,
}
export enum SubLabelTheme {
  Breadcrumb = `breadcrumb`,
  Small = `small`,
  Soft = `soft`,
  Strong = `strong`,
}

/**
 * Too many existing instances of mp-select and mp-items-menu shove additional properties inside SectionItem.
 * To handle these cases we include {[key: string]: any} to allow any extra keys.
 * ExtendWithT has been implemented to slowly correct this overly-loose definition.
 * While abnormal, the approach allows the caller to override the any index signature, therefore making it strict.
 * Ex:
 * MyValueType = {value: string}
 * MyStrictSectionItem = SectionItem<MyValueType, {moreData: string}>;
 * MyStrictSection = Section<MyValueType, SectionItem>;
 *
 * If you do not need to extend SectionItem but still want strict typing use NoExtension;
 * PlainStrictSectionItem = SectionItem<MyValueType, NoExtension>;
 */

// We disable non-nullish values for typing safety.
// However, In this instance we want to use it to signify that there is no values to extends.
// We declare the type here to prevent the need for lint-disables for it's users.
// eslint-disable-next-line @typescript-eslint/ban-types
export type NoExtension = {};

export type Section<T = any, ExtendWithT = Obj, SectionItemT = SectionItem<T, ExtendWithT>> = {
  /** Section's label. If not present, only a horizontal separator rule is shown */
  label?: string;

  /** svg-icon shown to the right of the section label */
  icon?: string;

  /** The items inside the section */
  items?: SectionItemT[];

  /** A spinny is shown inside the section if isLoading is true */
  isLoading?: boolean;

  /** Number of items to show per section. If more items than showLimit a 'Show All' expander is shown */
  showLimit?: number;

  /** if we want to display all selections, but only want to show the ability to select one */
  displayAllSelectOne?: boolean;

  /** Allows a section to be included in all logic but not rendered in the menu.
      This is useful for conditionally showing after a response from the menuiitem */
  isHidden?: boolean;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => VNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => VNode;

  /** Customize the icon next to the section label. Only available if items are passed via props */
  sectionLabelIconRenderer?: (item: Section) => VNode;

  /** Hide the top section divider */
  hideTopDivider?: boolean;
};

export type SectionItem<ValueT = any, ExtendWithT = Obj> = {
  /** Item's label. This should always be present */
  label: string;

  /** A shorter version of label, used in menu summaries */
  shortLabel?: string;

  /** label is truncated by default. wrapLabel: true turns on wrapping so user can read full label */
  wrapLabel?: boolean;

  /** A short blurb / description about item that is shown on another line */
  subLabel?: string;

  /** subLabel is truncated by default. wrapSubLabel: true turns on wrapping so user can read full description */
  wrapSubLabel?: boolean;

  /** Theme styling for the label. */
  labelTheme?: LabelTheme;

  /** Theme styling for the sublabel. */
  subLabelTheme?: SubLabelTheme;

  /** svg-icon shown besides the label */
  icon?: string;

  /** is the icon highlighted  */
  highlightIcon?: boolean;

  /** By default icon is shown on left, but user can specify right icon e.g BETA icon */
  iconSide?: `left` | `right`;

  /** Can the item be selected. Item is greyed out if disabled */
  isDisabled?: boolean;

  /** Marks the item as selected */
  isSelected?: boolean;

  /** Should '>' caret be shown besides the item. Used to indicate sub items */
  hasCaret?: boolean;

  /** Should '>' caret be shown even when item is not hovered/focused */
  persistCaret?: boolean;

  /** Wrap the item in an anchor tag with a link */
  href?: string;

  /** anchor tag target */
  target?: string;

  /** item is not clickable */
  isReadOnly?: boolean;

  /**
   * Also used to indicate sub items, but either item or sub items can be selected.
   * We should re-think this with design. Not the most intuitive UX experience
   */
  hasPill?: boolean;

  /** Label of the pill e.g. 'Properties' or 'Typecast' */
  pillLabel?: string;

  /** Tooltip shown on item hover for additional information */
  tooltip?: TooltipInfo;

  /** Whether the pill is disabled. */
  isPillDisabled?: boolean;

  /** Hides selectable controls and indicates that this item is not a list item */
  isControlItem?: boolean;

  /** Optional value included in select event */
  value?: ValueT;

  /** Color of the checkbox if it's a checkbox */
  color?: string;

  /** Defines nested sections */
  subSections?: Array<Section<ValueT, ExtendWithT, SectionItem<ValueT>>>;

  /** Defines any referenced named slots */
  slot?: string;

  /** Customize the icon of the item. Only available if items are passed via props */
  iconRenderer?: (item: SectionItem) => VNode;

  /** Allows to add custom actions at the right of the item. Only available if items are passed via props */
  actionsRenderer?: (item: SectionItem) => VNode;
} & ExtendWithT;

export interface TooltipInfo extends Omit<TooltipAttrs, 'placement'> {
  /** Placement of the tooltip within the section, only allow left or right */
  placement?: Placement.Left | Placement.Right;
}

export interface WrappedSectionItem<T = any> extends SectionItem<T> {
  index?: number;
  labelMatch?: SearchMatchResult | null;
  subLabelMatch?: SearchMatchResult | null;
  customMatch?: SearchMatchResult | null;
  wrapped: SectionItem;
}

export interface WrappedSection<T = any> extends Section<T> {
  wrappedSectionIndex?: number;
  items: WrappedSectionItem[];
}

export interface DropdownPositioning {
  horizontal?: Position.LEFT | Position.RIGHT;
  vertical?: Position.TOP | Position.BOTTOM;
  styles: {[key: string]: string};
}

export interface ItemsMenuState {
  contextPanelTransform: SectionItemContextPanelTransform;
  customSearchMatcherFn: CustomSearchMatcherFn;
  focusedItemIndex: number;
  fullyShownSections: Obj<boolean>; // Map to track which sections have 'show all' clicked
  isPillFocused: boolean;
  isSubItemsOpen: boolean;
  keyedItems: {[labelKey: string]: SectionItem};
  progressiveListSize: number;
  sectionsFromInputFn: SectionsFromSearchInputFn;
  sections: Section[];
  selectionState: SelectionState;
  tooltipData: ContextPanelData;
}

export interface ElementRefs {
  subItemsEl?: HTMLElement;
  containerEl?: HTMLElement;
}

export interface ItemsMenuAttrs {
  'disable-search-sort'?: boolean;
  'empty-message'?: string;
  'empty-search-results-message'?: string;
  multiple?: boolean;
  'search-exact'?: boolean;
  'search-filter'?: string;
  sections?: Section[];
  'sections-hint'?: string;
  'show-csv-matches'?: boolean;
  'show-select-all'?: boolean;
  'show-select-all-matching'?: boolean;
  'select-all-disabled'?: boolean;
  'select-all-tooltip'?: TooltipInfo;
  'select-all-matching-disabled'?: boolean;
  'enforce-limit-when-searching'?: boolean;
  'unfocus-on-mouseleave'?: boolean;
  'align-all-text'?: boolean;
}

export interface ItemsMenuJsxProps {
  attrs?: ItemsMenuAttrs;
  props?: {
    sectionsFromInputFn?: SectionsFromSearchInputFn;
    contextPanelTransform?: SectionItemContextPanelTransform;
    customSearchMatcherFn?: CustomSearchMatcherFn;
    sections?: Section[];
    keydownProxyEl?: HTMLElement;
  };
  on?: {
    navigateBack?: (ev?: CustomEvent) => void;
    selectAll?: (ev?: CustomEvent) => void;
    selectAllMatching?: (ev?: CustomEvent) => void;
    clickItem?: (ev?: ItemClickEvent) => void;
    clickExpanderItem?: (ev?: ExpanderItemClickEvent) => void;
    clickPill?: (ev?: CustomEvent) => void;
    focusItem?: (ev?: ItemFocusEvent) => void;
    draw?: (ev?: CustomEvent) => void;
    change?: (ev?: CustomEvent) => void;
  };
}

export type EventHandler = (ev: CustomEvent) => void;
export type ItemHandler = (item: WrappedSectionItem) => void;
export type ItemAndEventHandler = (item: WrappedSectionItem, ev: Event) => void;
export type HTMLElementHandler = (el: HTMLElement) => void;

export type ExpanderItemClickEvent<T = any> = CustomEvent<{
  section: Section<T>;
  sectionIdx: number;
}>;

export type ItemFocusEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<{
  item: SectionItemT;
  section: SectionT;
}>;

export type ItemClickEvent<T = any, SectionT = Section<T>, SectionItemT = SectionItem<T>> = CustomEvent<
  WrappedSectionItem<T> & {
    topScrollPosition: number;
    selectionState: SelectionState;
    modifierKeys: Obj<boolean>;
    item: SectionItemT;
    section: SectionT;
  }
>;
